import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzModalService } from 'ng-zorro-antd/modal';
import { environment } from '../../../../environments/environment';
import { AlertMessageService } from 'shared/core';

import { ActivatedRoute, Params, Router } from '@angular/router';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { PurchaseOrderModel, SupplierModel, ProformaInvoiceModel, BankMasterModel, DocumentCode, ActiveDocumentVersionVm } from 'shared/models';
import { CompanyInfo } from 'shared/models';
import { DocumentService } from 'shared/core';
// Static ES import — esbuild does not support runtime require() of this CommonJS module
import numberToWords from 'number-to-words';

@Component({
    selector: 'app-proformaPrint',
    templateUrl: './proformaPrint.component.html',
    styleUrls: ['./proformaPrint.component.css'],
    standalone: false
})
export class proformaPrintComponent implements OnInit {
  ApiUrl = environment.Api_Url;
  ProformaInvoiceList: ProformaInvoiceModel[] = [];
  ProformaInvoice!: ProformaInvoiceModel;
  NewSupplier: SupplierModel = new SupplierModel();
  isVisible = false;
  isLoading: boolean = false;
  isTableLoading: boolean = false;
  typeList: any[] = [];
  PopUpTitle: string = 'Add New Production';
  searchValue = '';
  visible = false;
  private route$: Subscription = new Subscription();
  BankList: BankMasterModel[] = [];
  Bank: BankMasterModel = new BankMasterModel;
  Poid = 0;
  TotalWord = '';
  GTotal = '';
  Company = {
    CompanyName: '',
    Contact: '',
    Email: '',
    Address: '',
    GST: '',
  };

  // Document version for IATF compliance
  ActiveDocumentVersion: ActiveDocumentVersionVm;

  // Print is gated behind a loading view until PI + Bank list + document version are ALL
  // loaded, so bank details never render blank (previously a Bank/PI load race printed
  // before the bank list arrived, requiring a manual refresh).
  loading = true;
  loadingMessage = 'Preparing Proforma Invoice for printing…';
  errorMessage = '';

  count: number;

  constructor(
    private fb: UntypedFormBuilder,
    public http: HttpClient,
    private alertService: AlertMessageService,
    private modalService: NzModalService,
    private route: ActivatedRoute,
    private router: Router,
    private documentService: DocumentService
  ) { }

  ngOnInit() {
    this.route$ = this.route.params.subscribe((params: Params) => {
      this.Poid = params['id'];
    });
    this.Company.CompanyName = CompanyInfo.CompanyName;
    this.Company.Contact = CompanyInfo.Contact;
    this.Company.Email = CompanyInfo.Email;
    this.Company.Address = CompanyInfo.Address.replace('\n', '<br>');
    this.Company.GST = CompanyInfo.GST;
    // this.Company.Website = CompanyInfo.Website;
    this.LoadPrintData();
  }

  // Load PI + bank list + document version together and only render/print once ALL are in.
  // Resolving Bank from BankList requires the list to be present, so these must not race.
  LoadPrintData() {
    this.loading = true;
    const banks$ = this.http.get<BankMasterModel[]>(this.ApiUrl + 'bank/getallbanks');
    const pi$ = this.http.get<ProformaInvoiceModel>(this.ApiUrl + 'proformainvoice/getproformainvoicebyid/' + this.Poid);
    // Document version (IATF stamp) is optional — a failure must not block the print.
    const docVersion$ = this.documentService.getActiveVersionByCode(DocumentCode.ProformaInvoice)
      .pipe(catchError(() => of(null)));

    forkJoin({ banks: banks$, pi: pi$, docVersion: docVersion$ }).subscribe({
      next: ({ banks, pi, docVersion }) => {
        if (!pi || !pi.ProformaInvoiceId) {
          this.showError('Proforma Invoice not found.');
          return;
        }
        // A Proforma Invoice is a customer-facing document: it must not leave the building
        // before the approval is on record. NULL status = grandfathered pre-approval-flow PI.
        // Mirrors the PO print guard, and covers a hand-typed /proformaPrint/:id URL.
        const status = pi.Status ?? 'Approved';
        if (status !== 'Approved') {
          this.router.navigate(['/home/unauthorized']);
          return;
        }
        this.BankList = banks || [];
        this.ActiveDocumentVersion = docVersion;
        this.ProformaInvoice = pi;
        this.Bank = this.BankList.find(x => x.BankId == this.ProformaInvoice.BankId) || this.Bank;

        this.ProformaInvoice.TermsCondition = (this.ProformaInvoice.TermsCondition || '').replaceAll('\n', '<br>');

        const gt = this.CalculateFinalInvoiceTotal();
        this.TotalWord = numberToWords.toWords(gt).toUpperCase();
        this.GTotal = gt?.toFixed(2);

        this.ProformaInvoice.TotalPrice = parseFloat(this.ProformaInvoice.TotalPrice?.toFixed(2));
        this.ProformaInvoice.Moqtotal = parseFloat(this.ProformaInvoice.Moqtotal?.toFixed(2));
        this.ProformaInvoice.Gst = parseFloat(this.ProformaInvoice.Gst?.toFixed(2));
        this.ProformaInvoice.Discount = parseFloat(this.ProformaInvoice.Discount?.toFixed(2));

        this.loading = false;
        // Let Angular render the now-complete content before opening the print dialog.
        setTimeout(() => {
          window.print();
          window.onafterprint = () => window.close();
        }, 300);
      },
      error: () => {
        this.showError('Failed to load the Proforma Invoice. Please try again.');
      }
    });
  }

  private showError(message: string) {
    this.errorMessage = message;
    this.loading = false;
    setTimeout(() => window.close(), 4000);
  }

  CalculateGrandTotal(data: any[], column: string) {
    var total = 0;
    switch (column) {
      case "Quantity": {
        total = parseFloat((data.reduce((sum, current) => sum + parseFloat(current.Quantity?.toString() || "0"), 0))?.toFixed(2)) || 0;
        break;
      }
      case "Total Price": {
        total = parseFloat((data.reduce((sum, current) => sum + (parseFloat(current.Quantity?.toString() || "0") * parseFloat(current.Price?.toString() || "0")), 0))?.toFixed(2)) || 0;
        break;
      }
    }
    return parseFloat(total.toFixed(2));
  }

  calculateDiscountAmount(): number {
    if (!this.ProformaInvoice) return 0;
    const subTotal = this.CalculateGrandTotal(this.ProformaInvoice.ProformaInvoiceItem, 'Total Price');
    const discountPercent = Number(this.ProformaInvoice.Discount) || 0;
    return (subTotal * discountPercent) / 100;
  }

  CalculateFinalInvoiceTotal(): number {
    if (!this.ProformaInvoice) return 0;
    const subTotal = this.CalculateGrandTotal(this.ProformaInvoice.ProformaInvoiceItem, 'Total Price');
    const moqAmount = Number(this.ProformaInvoice.Moqtotal) || 0;
    const gstAmount = Number(this.ProformaInvoice.Gst) || 0;
    const discountAmount = this.calculateDiscountAmount();
    const roundOff = Number(this.ProformaInvoice.RoundOff) || 0;
    const advanceAmount = Number(this.ProformaInvoice.AdvanceAmount) || 0;

    return subTotal + moqAmount + gstAmount - discountAmount + roundOff - advanceAmount;
  }

}
